import { MenuItem, MenuList } from "@mui/material";
import type { RenderFilterProps } from "@shared/frontend/hyper-table/filter-types";
import { generateHyperFilterFromPath } from "@shared/helpers/sql-relations";
import React, { useCallback, useMemo } from "react";
import { getValueList, repo } from "remult";
import { useHyperTable } from "../../../HyperTable";
import CustomFilter from "./CustomFilter";
import {
  getFinalChildEntityFromFieldPath,
  type EntityFilterProps,
} from "./helpers";
import { CheckboxIcon } from "@shared/frontend/icons";

type LiteralFilterProps<Entity> = EntityFilterProps<Entity>

const LiteralFilter = <Entity extends any>(
  props: LiteralFilterProps<Entity>,
) => {
  const { filter, closeMenu } = props;
  const { getEntity } = useHyperTable<Entity>();

  const options = useMemo(() => {
    const entityRepo = repo(
      getFinalChildEntityFromFieldPath(getEntity(), filter.fieldName),
    );
    const fieldMetadata = entityRepo.metadata.fields.find(
      filter.fieldName.split(".").pop() as any,
    );

    if (!fieldMetadata)
      throw new Error(
        `Field ${filter.fieldName.toString()} not found in entity "${entityRepo.metadata.key}"`,
      );

    return (
      getValueList(fieldMetadata)?.map((value) => {
        if (filter.getOptionCaption) {
          return {
            caption: filter.getOptionCaption!(
              typeof value === "string" ? value : ((value as any).id as string),
            ),
            id: value as string,
          };
        }
        return typeof value === "string"
          ? {
            caption:
              fieldMetadata.options.displayValue?.(undefined!, value) ||
              value,
            id: value,
          }
          : {
            caption: (value as any).caption as string,
            id: (value as any).id as string,
          };
      }) ?? []
    );
  }, [getEntity(), filter.fieldName]);

  const handleSave = useCallback(
    (onSave: RenderFilterProps<Entity>["onSave"], value: string) => {
      onSave(
        (prev) => {
          const copy = Array.isArray(prev.meta.selectedOptions)
            ? [...prev.meta.selectedOptions]
            : [];

          if (copy.includes(value)) {
            copy.splice(copy.indexOf(value), 1);
          } else {
            copy.push(value);
          }

          const entity = getEntity();
          const isRelationalField =
            "hyperFilter" in entity && filter.fieldName.includes(".");

          return {
            value: copy.length
              ? isRelationalField
                ? (entity as any).hyperFilter(
                  generateHyperFilterFromPath(entity, filter.fieldName, {
                    $in: copy,
                  }),
                )
                : {
                  ...prev.value,
                  [filter.fieldName]: {
                    $in: copy,
                  },
                }
              : {},
            meta: {
              ...prev.meta,
              selectedOptions: copy,
            },
          };
        },
        { closeOnSave: false },
      );
    },
    [filter.fieldName],
  );

  return (
    <CustomFilter<Entity>
      closeMenu={closeMenu}
      filter={{
        icon: "number",
        ...filter,
        renderFilter: useCallback(
          ({ meta, onSave }) => (
            <MenuList>
              {options.map((option) => (
                <MenuItem
                  key={option.id}
                  sx={{ gap: "0.8rem" }}
                  onClick={() => handleSave(onSave, option.id)}
                >
                  <CheckboxIcon
                    checked={
                      Array.isArray(meta.selectedOptions) &&
                      meta.selectedOptions.includes(option.id)
                    }
                    className="size-4"
                    aria-hidden="true"
                  />
                  <span>{option.caption}</span>
                </MenuItem>
              ))}
            </MenuList>
          ),
          [handleSave, options],
        ),
      }}
    />
  );
};

export default LiteralFilter;
